import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { accountOptionLabel } from '../lib/account-label.js';

/**
 * SCRN 53 — Account History (CCF-103).
 *
 * Faithful port of the Figma "update account history" export. The agent picks
 * an account (name + last 4 digits), then sees the account header (number /
 * status / type) and a READ-ONLY history table: Date, Time, Transaction
 * display, History description, Teller name, Station. The screen is view-only —
 * no further actions (per CCF-103's "Account History screen is view-only").
 *
 * Data sourcing: backed by the LIVE R4 `account/history` op (verified against
 * `cc-apigw-staging.ptsb.ie` — #166; the earlier "no R4 op / blocked on
 * UBAG-721" assumption was stale). The BFF sends the R4-mandatory `CardNumber`
 * from the verified session and maps the gateway
 * `EPortalResponseContent.HistDetails_List` onto the `AccountHistory` rows this
 * screen renders. A gateway business decline (`SuccessErrorInd:'N'`) comes back
 * as `available:false` + an inline message; this screen renders that message
 * inline rather than fabricating rows. See the BFF `/accounts/history` route +
 * `server/src/banking/client.ts` (`parseAccountHistory`), behind the same
 * Teller-ID + RBAC + verification + account-ownership gates as the other
 * account-scoped reads. Called via the generated typed client:
 *   - accounts: POST /api/customers/accounts
 *   - history:  POST /api/accounts/history
 *
 * The shared agent banner lives in the app shell, so this screen renders only
 * the `<main>` content.
 */

interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  Nickname?: string;
  Status?: string;
}

/** A single history line this screen renders (the BFF returns these fields). */
interface HistoryEntry {
  Date?: string;
  Time?: string;
  TransactionDisplay?: string;
  Description?: string;
  TellerName?: string;
  Station?: string;
}

interface AccountHistoryResponse {
  available?: boolean;
  message?: string;
  AccountNumber?: string;
  AccountType?: string;
  Status?: string;
  Entries?: HistoryEntry[];
  /** Pagination cursor (#791): more rows remain beyond this page. */
  hasNextPage?: boolean;
  /** Opaque gateway cursor to fetch the next page (#791). */
  nextKey?: string;
}

const UNAVAILABLE_MESSAGE =
  'Account history is not yet available from the bank gateway.';

/**
 * Rows shown per page (SCRN 53 / CCF-103, #791, per @dylan219: "20 per page").
 * The screen retrieves the gateway's first ~100-row page on account select and
 * windows it 20 at a time (pages 1–5); it only re-requests the gateway (via the
 * returned `nextKey`) when the agent pages BEYOND the rows already retrieved.
 */
const PAGE_SIZE = 20;

/** Accessible dropdown, faithful to the export's listbox pattern. */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label={label}
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value || 'placeholder'}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function AccountHistoryScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  // Account header (number / status / type) from the first loaded page.
  const [header, setHeader] = useState<{
    AccountNumber?: string;
    AccountType?: string;
    Status?: string;
  } | null>(null);
  // Accumulated history rows across the gateway pages fetched so far (#791). The
  // first load retrieves ~100; more are appended only when the agent pages past
  // them.
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  // Gateway pagination cursor (#791): whether more rows remain, and the opaque
  // key to fetch them. `hasMoreFromGateway` gates the "call the gateway again"
  // branch of Next — the ONLY place we re-request beyond the initial retrieval.
  const [gatewayNextKey, setGatewayNextKey] = useState('');
  const [hasMoreFromGateway, setHasMoreFromGateway] = useState(false);
  // 0-based display page (20 rows/page) windowed over `entries`.
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  // True while fetching the NEXT gateway block on a Next-past-loaded-rows.
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState('');
  // Set when the BFF signals the load is unavailable/declined: a gateway
  // business decline (SuccessErrorInd 'N') relayed inline, or the defensive
  // HTTP 501 fallback. Render an explicit message rather than any history rows.
  const [unavailable, setUnavailable] = useState('');
  // Monotonic sequence so a slow earlier load can't overwrite a newer one.
  const seqRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    // The customer context changed (or first mount). Drop any customer-scoped
    // state from the PREVIOUS context and invalidate any in-flight history
    // request (bump seqRef) so a late response can't render under the new
    // customer — otherwise the prior customer's history would keep showing.
    seqRef.current += 1;
    setAccounts([]);
    setSelectedAccount('');
    setHeader(null);
    setEntries([]);
    setGatewayNextKey('');
    setHasMoreFromGateway(false);
    setPage(0);
    setUnavailable('');
    setError('');
    async function load(): Promise<void> {
      if (!hasContext) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/accounts',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (apiError || !data) {
          setError('Could not load accounts.');
          return;
        }
        setAccounts((data.Accounts ?? []) as unknown as AccountRow[]);
      } catch {
        if (!cancelled) setError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  /**
   * Load the selected account's FIRST page of read-only history (CCF-103) — the
   * gateway's first ~100-row block. Resets the accumulated rows + pager.
   */
  async function loadInitial(accountNumber: string): Promise<void> {
    if (!accountNumber) return;
    const seq = ++seqRef.current;
    setLoading(true);
    setError('');
    setUnavailable('');
    setHeader(null);
    setEntries([]);
    setGatewayNextKey('');
    setHasMoreFromGateway(false);
    setPage(0);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/history', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, accountNumber },
      });
      if (seq !== seqRef.current) return;
      // The BFF signals an unavailable / declined load with `available:false`
      // and an inline message (#166): either a gateway business decline relayed
      // in a 200 envelope (SuccessErrorInd 'N' → code+message) or the defensive
      // HTTP 501 fallback. Surface the message inline beside the action rather
      // than as a generic error or any (non-existent) rows.
      const body = (data ??
        (apiError as unknown)) as AccountHistoryResponse | null;
      if (response.status === 501 || body?.available === false) {
        setUnavailable(body?.message ?? UNAVAILABLE_MESSAGE);
        return;
      }
      if (apiError || !data) {
        // CCF-103 unhappy path: backend error returning details from Unibanks.
        setError(
          response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : 'Could not load the account history. Please try again.',
        );
        return;
      }
      const resp = data as unknown as AccountHistoryResponse;
      setHeader({
        AccountNumber: resp.AccountNumber,
        AccountType: resp.AccountType,
        Status: resp.Status,
      });
      setEntries(resp.Entries ?? []);
      setGatewayNextKey(resp.nextKey ?? '');
      setHasMoreFromGateway(!!resp.hasNextPage);
    } catch {
      if (seq === seqRef.current)
        setError('Could not load the account history. Please try again.');
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }

  /**
   * Fetch the NEXT gateway block and append it (#791). This is the ONLY place
   * the gateway is re-requested — invoked from Next when the agent pages BEYOND
   * the rows already retrieved. Does NOT bump `seqRef` (it extends the current
   * load); a customer/account change invalidates it via the seq guard.
   */
  async function fetchMore(): Promise<void> {
    if (!selectedAccount || !hasMoreFromGateway || !gatewayNextKey) return;
    const seq = seqRef.current;
    setPageLoading(true);
    setError('');
    try {
      const { data, error: apiError } = await api.POST('/accounts/history', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: selectedAccount,
          nextKey: gatewayNextKey,
        },
      });
      if (seq !== seqRef.current) return;
      if (apiError || !data) {
        setError('Could not load more account history. Please try again.');
        return;
      }
      const resp = data as unknown as AccountHistoryResponse;
      setEntries((prev) => [...prev, ...(resp.Entries ?? [])]);
      setGatewayNextKey(resp.nextKey ?? '');
      setHasMoreFromGateway(!!resp.hasNextPage);
      // Advance to the first of the newly-loaded rows.
      setPage((p) => p + 1);
    } catch {
      if (seq === seqRef.current)
        setError('Could not load more account history. Please try again.');
    } finally {
      if (seq === seqRef.current) setPageLoading(false);
    }
  }

  function onAccountChange(value: string): void {
    setSelectedAccount(value);
    setUnavailable('');
    void loadInitial(value);
  }

  const selected = accounts.find((a) => a.AccountNumber === selectedAccount);
  const loadedPages = Math.max(1, Math.ceil(entries.length / PAGE_SIZE));
  // Clamp so a shrink (fresh load) can never leave `page` past the end.
  const currentPage = Math.min(page, loadedPages - 1);
  const pageStart = currentPage * PAGE_SIZE;
  const pageRows = entries.slice(pageStart, pageStart + PAGE_SIZE);
  const showingFrom = entries.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + PAGE_SIZE, entries.length);
  // Next is enabled when there's another loaded page OR the gateway has more
  // rows to fetch (the "beyond the initial 100" case).
  const canNext =
    currentPage + 1 < loadedPages || (hasMoreFromGateway && !!gatewayNextKey);

  /** Next: page within loaded rows, else fetch the next gateway block (#791). */
  function onNext(): void {
    if (currentPage + 1 < loadedPages) {
      setPage(currentPage + 1);
    } else if (hasMoreFromGateway && gatewayNextKey) {
      void fetchMore();
    }
  }

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="account-history-heading"
    >
      <div className="mb-4">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="account-history-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8"
        >
          Account History details
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {/* Account selection — name + last 4 digits (CCF-82/103). */}
      <div className="mb-6">
        <div className="w-[360px]">
          <Dropdown
            id="account-history-account"
            label="Select account to view"
            placeholder="Select"
            value={selectedAccount}
            onChange={onAccountChange}
            options={accounts.map((a) => ({
              value: a.AccountNumber,
              label: accountOptionLabel(a),
            }))}
          />
        </div>
      </div>

      {loading && (
        <p
          role="status"
          className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] m-0"
        >
          Loading…
        </p>
      )}

      {/* Load unavailable/declined (gateway SuccessErrorInd 'N' relayed inline,
          or the defensive HTTP 501 fallback): show the message — never
          fabricated rows. */}
      {selectedAccount && !loading && unavailable && (
        <div
          role="status"
          className="max-w-[1110px] border-2 border-[#dad7d3] bg-white px-6 py-5 font-['Arial',sans-serif] text-[16px] text-[#1d252c]"
        >
          {unavailable}
        </div>
      )}

      {/* Account history details — shown when an account is selected. */}
      {selectedAccount && !loading && !unavailable && header && (
        <div className="border-2 border-[#dad7d3] bg-white max-w-[1110px]">
          {/* Header row with account info (CCF-103). */}
          <div className="flex flex-wrap items-center gap-8 px-6 py-4 bg-[#f3f4f6] border-b-2 border-[#dad7d3]">
            <div className="flex gap-2">
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                Account number:
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                {header.AccountNumber ?? selected?.AccountNumber ?? '—'}
              </p>
            </div>
            <div className="flex gap-2">
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                Status:
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                {header.Status ?? selected?.Status ?? '—'}
              </p>
            </div>
            <div className="flex gap-2">
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                Account type:
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                {header.AccountType ?? selected?.AccountType ?? '—'}
              </p>
            </div>
          </div>

          {/* Read-only history table (CCF-103). */}
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#e5e7eb] border-b border-[#dad7d3]">
                {[
                  'Date',
                  'Time',
                  'Transaction display',
                  'History description',
                  'Teller name',
                  'Station',
                ].map((col) => (
                  <th
                    key={col}
                    scope="col"
                    className="text-left text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] px-4 py-3 border-r border-[#dad7d3] last:border-r-0"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-[16px] font-['Arial',sans-serif] text-[#6b7280]"
                  >
                    No account history is available for this account.
                  </td>
                </tr>
              ) : (
                pageRows.map((entry, index) => (
                  <tr
                    key={pageStart + index}
                    className="border-b border-[#dad7d3] align-top"
                  >
                    <td className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] px-4 py-3 border-r border-[#dad7d3] whitespace-nowrap">
                      {entry.Date ?? '—'}
                    </td>
                    <td className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] px-4 py-3 border-r border-[#dad7d3] whitespace-nowrap">
                      {entry.Time ?? '—'}
                    </td>
                    <td className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] px-4 py-3 border-r border-[#dad7d3]">
                      {entry.TransactionDisplay ?? '—'}
                    </td>
                    <td className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] px-4 py-3 border-r border-[#dad7d3] leading-[1.4]">
                      {entry.Description ?? '—'}
                    </td>
                    <td className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] px-4 py-3 border-r border-[#dad7d3]">
                      {entry.TellerName ?? '—'}
                    </td>
                    <td className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] px-4 py-3 whitespace-nowrap">
                      {entry.Station ?? '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pager (#791, per @dylan219): 20 rows per page with Previous / Next.
              The first ~100 rows are windowed client-side (pages 1–5); Next only
              calls the gateway (via `nextKey`) once the agent pages BEYOND the
              rows already retrieved. */}
          {entries.length > 0 && (
            <div className="flex items-center gap-4 px-6 py-4 border-t-2 border-[#dad7d3]">
              <button
                type="button"
                onClick={() => setPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0 || pageLoading}
                aria-label="Previous page of account history"
                className="inline-flex items-center justify-center px-5 py-2 min-h-[44px] bg-[#FF703E] text-[#1D252C] font-['Arial',sans-serif] text-[14px] font-bold rounded-[4px] shadow-sm hover:bg-[#e55a2a] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#FF703E]"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={onNext}
                disabled={!canNext || pageLoading}
                aria-label="Next page of account history"
                className="inline-flex items-center justify-center px-5 py-2 min-h-[44px] bg-[#FF703E] text-[#1D252C] font-['Arial',sans-serif] text-[14px] font-bold rounded-[4px] shadow-sm hover:bg-[#e55a2a] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#FF703E]"
              >
                {pageLoading ? 'Loading…' : 'Next'}
              </button>
              <span
                className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]"
                aria-live="polite"
                data-testid="account-history-pager-status"
              >
                Showing {showingFrom}–{showingTo}
                {hasMoreFromGateway ? '+' : ` of ${entries.length}`}
              </span>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
